/*
 * UrlHashManager
 * Provides a consistent interface for managing the url hash from various places (eg. components).
 * The class holds an internal object representation of the key/value pairs in the URL hash.
 * Changing the key or value will update the URL hash representation.
 */

export class UrlHashManager {
  constructor(element, key, options = {}) {
    this.element = element;
    this.key = key;

    this.delimiter = options.delimiter || "&";
    this.operator = options.operator || "=";

    this.changeEventName = options.changeEventName || "Rankers.UrlHashManager:change";

    this.cruft = [];
    this.data = [];
    this.extract();
    window.addEventListener("hashchange", this.handleUrlHashChange.bind(this));
  }

  handleUrlHashChange() {
    const oldValue = this.data[this.key];
    this.extract();
    const newValue = this.data[this.key];
    if (oldValue !== newValue) this._dispatchUrlHashChange(oldValue, newValue);
  }

  extract() {
    const result = parseUrlHashString(window.location.hash, {
      delimiter: this.delimiter,
      operator: this.operator,
    });
    this.data = result.data;
    this.cruft = result.cruft;
  }

  setValue(value) {
    const oldValue = this.data[this.key];
    if (oldValue !== value) {
      this.data[this.key] = value;
      this._dispatchUrlHashChange(oldValue, value);
    }
    this.render();
  }

  getValue() {
    return this.data[this.key];
  }

  render() {
    window.location.hash = this.getUrlHashRepresentation();
  }

  getUrlHashRepresentation() {
    const dataPairs = Object.entries(this.data).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,)
    return this.cruft.concat(dataPairs).join(this.delimiter);
  }

  _dispatchUrlHashChange(oldValue, newValue) {
    const event = new CustomEvent(this.changeEventName, {
      detail: {
        key: this.key,
        oldValue: oldValue,
        newValue: newValue
      }
    });
    this.element.dispatchEvent(event);
  }
}


function parseUrlHashString(hashString, options = {}) {
  const delimiter = options.delimiter || "&";
  const operator = options.operator || "=";

  hashString = decodeURIComponent(hashString);

  // Remove the '#' character and split the hash into key-value pairs using the provided delimiter
  let pairs = hashString.startsWith("#")
    ? hashString.substring(1).split(delimiter)
    : hashString.split(delimiter);

  let result = {};
  let cruft = []; // Array to hold anything that doesn't conform to a key/value pair.

  pairs.forEach((pairString) => {
    if (!pairString) return;

    const pair = pairString.split(operator);

    if (pair.length === 2) {
      const key = decodeURIComponent(pair[0]);
      const value = decodeURIComponent(pair[1]);
      result[key] = value;
    } else if (pair.length > 0) {
      cruft.push(pairString);
    }
  });

  return { data: result, cruft: cruft };
}
